import React, { useState, useEffect, useMemo } from "react";
import { CalendarDays, X, ArrowRight, Ship, BookOpen } from "lucide-react";

// Tarih değiştirme + Defter/Tekne taşıma modalı.
// props: open, onClose, onConfirm({newDate, categoryId, boatId}), currentDate (YYYY-MM-DD),
//        title, defters [{id,name,boats:[{id,name}]}], currentCategoryId, currentBoatId
export default function RescheduleModal({ open, onClose, onConfirm, currentDate, title,
  defters = [], currentCategoryId = null, currentBoatId = null }) {
  const [val, setVal] = useState(currentDate || "");
  const [catId, setCatId] = useState(currentCategoryId);
  const [boatId, setBoatId] = useState(currentBoatId);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setVal(currentDate || "");
      setCatId(currentCategoryId);
      setBoatId(currentBoatId);
      setSaving(false);
    }
  }, [open, currentDate, currentCategoryId, currentBoatId]);

  const boatsForCat = useMemo(() => {
    const df = defters.find((d) => d.id === Number(catId));
    return df ? df.boats : [];
  }, [defters, catId]);

  if (!open) return null;

  const shift = (days) => {
    const base = new Date((currentDate || val) + "T00:00:00");
    base.setDate(base.getDate() + days);
    setVal(base.toISOString().slice(0, 10));
  };

  const fmt = (d) => {
    if (!d) return "-";
    try {
      return new Date(d + "T00:00:00").toLocaleDateString("tr-TR", { day: "numeric", month: "long", weekday: "short" });
    } catch { return d; }
  };

  const onCatChange = (v) => {
    const nc = Number(v);
    setCatId(nc);
    // defter değişince o defterin ilk teknesini seç (mevcut tekne o defterde değilse)
    const df = defters.find((d) => d.id === nc);
    const boats = df ? df.boats : [];
    if (!boats.some((b) => b.id === Number(boatId))) {
      setBoatId(boats.length ? boats[0].id : null);
    }
  };

  const dateChanged = val && val !== currentDate;
  const catChanged = catId != null && Number(catId) !== Number(currentCategoryId);
  const boatChanged = boatId != null && Number(boatId) !== Number(currentBoatId);
  const changed = dateChanged || catChanged || boatChanged;

  const handleSave = async () => {
    if (!changed) return;
    setSaving(true);
    await onConfirm({
      newDate: dateChanged ? val : (currentDate || val),
      categoryId: catChanged ? Number(catId) : null,
      boatId: (catChanged || boatChanged) && boatId != null ? Number(boatId) : null,
    });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose} data-testid="reschedule-modal-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-in"
        onClick={(e) => e.stopPropagation()} data-testid="reschedule-modal">
        <div className="flex items-center justify-between px-5 py-3.5 bg-violet-600 text-white">
          <div className="flex items-center gap-2">
            <CalendarDays className="w-5 h-5" />
            <span className="font-semibold text-sm">Tarih / Defter Değiştir</span>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-white/20" data-testid="reschedule-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {title && <div className="text-xs text-gray-500 truncate">{title}</div>}

          <div className="flex items-center justify-center gap-3 text-center">
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">Şu an</div>
              <div className="text-sm font-bold text-gray-700 mt-0.5">{fmt(currentDate)}</div>
            </div>
            <ArrowRight className={`w-5 h-5 shrink-0 ${dateChanged ? "text-violet-500" : "text-gray-300"}`} />
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">Yeni</div>
              <div className={`text-sm font-bold mt-0.5 ${dateChanged ? "text-violet-600" : "text-gray-300"}`}>{fmt(val)}</div>
            </div>
          </div>

          <input
            type="date"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            className="w-full border-2 border-violet-200 focus:border-violet-500 outline-none rounded-xl px-3 py-2.5 text-sm font-medium text-gray-800"
            data-testid="reschedule-date-input"
          />

          <div className="flex flex-wrap gap-2">
            {[{ l: "Yarın", d: 1 }, { l: "+2 gün", d: 2 }, { l: "+3 gün", d: 3 }, { l: "+1 hafta", d: 7 }].map((q) => (
              <button key={q.d} onClick={() => shift(q.d)}
                className="px-3 py-1.5 rounded-full text-xs font-semibold bg-violet-50 text-violet-700 hover:bg-violet-100 transition-colors"
                data-testid={`reschedule-quick-${q.d}`}>
                {q.l}
              </button>
            ))}
          </div>

          {/* Defter + Tekne seçici */}
          {defters.length > 0 && (
            <div className="space-y-2.5 pt-1 border-t border-gray-100">
              <div>
                <label className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 mb-1">
                  <BookOpen className="w-3.5 h-3.5" /> Defter
                </label>
                <select
                  value={catId ?? ""}
                  onChange={(e) => onCatChange(e.target.value)}
                  className="w-full border-2 border-gray-200 focus:border-violet-500 outline-none rounded-xl px-3 py-2 text-sm font-medium text-gray-800 bg-white"
                  data-testid="reschedule-defter-select">
                  {defters.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 mb-1">
                  <Ship className="w-3.5 h-3.5" /> Tekne
                </label>
                <select
                  value={boatId ?? ""}
                  onChange={(e) => setBoatId(Number(e.target.value))}
                  className="w-full border-2 border-gray-200 focus:border-violet-500 outline-none rounded-xl px-3 py-2 text-sm font-medium text-gray-800 bg-white"
                  data-testid="reschedule-boat-select">
                  {boatsForCat.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>
              {catChanged && (
                <div className="text-[11px] text-amber-600 font-medium" data-testid="reschedule-defter-warning">
                  Rezervasyon bu defterden çıkıp seçtiğiniz deftere taşınacak.
                </div>
              )}
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <button onClick={onClose}
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
              data-testid="reschedule-cancel-btn">
              Vazgeç
            </button>
            <button onClick={handleSave} disabled={!changed || saving}
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              data-testid="reschedule-save-btn">
              {saving ? "Taşınıyor..." : "Taşı"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
